import React, { useState } from "react";
import { Modal, Button, Form, InputGroup, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { FaPaperPlane } from "react-icons/fa";
import WebService from "../../Services/WebService";
import CurrencyInputField from "../Common/CurrencyInput/CurrencyInputField";

interface Props {
  show: boolean;
  onHide: () => void;
  requirement: any;
  onSuccess?: (quote?: any) => void;
}

const SubmitQuoteModal: React.FC<Props> = ({ show, onHide, requirement, onSuccess }) => {
  const [price, setPrice] = useState("");
  const [leadTime, setLeadTime] = useState("14");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  if (!requirement) return null;

  const reqTitle = requirement.title || requirement.product_name || "Business Requirement";
  const reqQty = requirement.quantity || requirement.qty || "Specified quantity";
  const reqCountry = requirement.country || "Global";
  const reqId = requirement.uuid || requirement.id;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!price.trim()) {
      toast.error("Please specify your quote unit price");
      return;
    }

    const token = localStorage.getItem("access_token");
    if (!token || token.trim() === "") {
      toast.error("You must be logged in to submit a quote.");
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("requirement_id", reqId);
    formData.append("unit_price", price.trim());
    formData.append("lead_time", leadTime);
    if (notes.trim()) {
      formData.append("remarks", notes.trim());
    }
    if (file) {
      formData.append("quotation_file", file);
    }

    WebService.postAPI({
      action: "network-hub/submit-quote",
      body: formData,
      isShowError: true,
    })
      .then((res: any) => {
        if (res && res.status) {
          toast.success(res.message || `Quote successfully submitted for ${reqTitle}! The buyer will review your quote.`);
          if (onSuccess) {
            onSuccess(res.info || res.quote);
          }
          onHide();
          setPrice("");
          setNotes("");
          setFile(null);
          setLeadTime("14");
        } else {
          toast.error(res?.message || "Failed to submit quote. Please try again.");
        }
      })
      .catch((err: any) => {
        console.error("Quote submission error:", err);
        toast.error(err?.response?.data?.message || err?.message || "Failed to submit quote. Please try again.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal show={show} onHide={loading ? undefined : onHide} centered>
      <Modal.Header closeButton={!loading} style={{ backgroundColor: "#6fb64c", color: "white" }}>
        <Modal.Title className="font-18 font-bold text-white">
          Submit Quote — {reqTitle}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body className="p-4">
          <div
            className="rounded-3 p-2.5 font-13 mb-3 d-flex align-items-center justify-content-between flex-wrap gap-2"
            style={{ backgroundColor: "#edf7e8", border: "1px solid #cde4bc", color: "#1f2933" }}
          >
            <span>Quoting for <strong>{reqQty}</strong> ({reqCountry})</span>
            <span className="badge rounded-pill text-white" style={{ backgroundColor: "#6fb64c", fontSize: "11px" }}>
              100% Free
            </span>
          </div>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold font-13">Offered Unit Price *</Form.Label>
            <CurrencyInputField
              id="quote_unit_price"
              placeHolder="Enter unit price"
              value={price}
              setValue={(val: string) => setPrice(val)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold font-13">Estimated Delivery Lead Time (Days)</Form.Label>
            <Form.Select value={leadTime} onChange={(e) => setLeadTime(e.target.value)} disabled={loading}>
              <option value="7">7 Days</option>
              <option value="14">14 Days</option>
              <option value="30">30 Days</option>
              <option value="45">45 Days</option>
              <option value="60">60+ Days</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold font-13">Offer Remarks / Product Specifications</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Include product quality grade, packaging type, payment terms, or discounts..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={loading}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold font-13">Attach Quotation File (Optional)</Form.Label>
            <Form.Control
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx"
              onChange={(e: any) => {
                if (e.target.files && e.target.files[0]) {
                  setFile(e.target.files[0]);
                }
              }}
              disabled={loading}
            />
            <Form.Text className="text-muted font-11">
              Accepted formats: PDF, DOCX, XLSX (Max 5MB, up to 15 pages)
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
            style={{ backgroundColor: "#6fb64c", borderColor: "#6fb64c", color: "#ffffff", fontWeight: "600" }}
            className="px-4 d-flex align-items-center gap-2"
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" /> Submitting...
              </>
            ) : (
              <>
                <FaPaperPlane /> Submit Quote
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default SubmitQuoteModal;
